/** Annuler / Rétablir de l'éditeur d'ordre de bataille : pile de brouillons (100 au plus). Une saisie de texte ne
 *  compte que pour une entrée par champ : les frappes d'un même champ (`group`) à moins de 600 ms l'une de l'autre se
 *  fondent dans la dernière entrée ; une pause, la perte du focus (`seal`) ou un autre champ en ouvrent une
 *  nouvelle. */

export interface History<T> {
  past: T[];
  present: T;
  future: T[];
  /** Champ de la dernière saisie fusionnable (null : la prochaine modification ouvre une entrée). */
  group: string | null;
  at: number;
}

export const HISTORY_LIMIT = 100;
export const GROUP_PAUSE_MS = 600;

export function createHistory<T>(present: T): History<T> {
  return { past: [], present, future: [], group: null, at: 0 };
}

/** Nouvel état ; `group` : champ saisi au clavier (fusion des frappes rapprochées). */
export function record<T>(history: History<T>, next: T, group: string | null = null, now = Date.now()): History<T> {
  if (next === history.present) return history;
  if (group && history.group === group && now - history.at < GROUP_PAUSE_MS && history.past.length > 0)
    return { ...history, present: next, future: [], at: now };
  return {
    past: [...history.past, history.present].slice(-HISTORY_LIMIT),
    present: next,
    future: [],
    group,
    at: now,
  };
}

/** Clôt la saisie en cours : la prochaine frappe ouvre une entrée. */
export function seal<T>(history: History<T>): History<T> {
  return history.group === null ? history : { ...history, group: null };
}

export const canUndo = <T>(history: History<T>) => history.past.length > 0;
export const canRedo = <T>(history: History<T>) => history.future.length > 0;

export function undo<T>(history: History<T>): History<T> {
  const previous = history.past[history.past.length - 1];
  if (previous === undefined) return history;
  return { past: history.past.slice(0, -1), present: previous, future: [history.present, ...history.future], group: null, at: 0 };
}

export function redo<T>(history: History<T>): History<T> {
  const [next, ...rest] = history.future;
  if (next === undefined) return history;
  return { past: [...history.past, history.present].slice(-HISTORY_LIMIT), present: next, future: rest, group: null, at: 0 };
}
